import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { useLazyGetLeadClientActionHistoryQuery } from '@app/api/ActionsHistory';
import type { GetLeadCardResponse } from '@app/api/Client/types';
import type { LeadCardHistoryItemData } from '@app/components/LeadCard/data';
import {
  formatLeadHistoryBoundary,
  formatLeadHistoryRangeEnd,
  formatLeadHistoryRangeStart,
  getLeadCommentHistoryKey,
  getLeadHistoryNow,
  getLeadHistoryStableKey,
  getPinnedCommentsAheadOfHistory,
  getPreviousHistoryRange,
  getValidHistoryFromMoment,
  LEAD_COMMENT_OBJECT_TYPE,
  LEAD_MSG_STATUS_PENDING,
  mergeLeadCommentHistoryItems,
  mergeLeadHistoryByStableKey,
} from '@app/components/LeadCard/ui/MessageHistory/lib';
import { WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import type { Moment } from 'moment';

interface UseLeadMessageHistoryParams {
  leadCardData: GetLeadCardResponse | undefined;
  selectedLeadId: number | null;
  isLeadCardPending: boolean;
  // Fired when a realtime history update matches the current lead clients, so
  // the owner can refresh lead-level state (e.g. answer timer fields).
  onRelevantClientHistory?(): void;
}

interface LeadCardHistoryRealtimeItem extends LeadCardHistoryItemData {
  ClientId?: number | string | null;
  ClientIds?: Array<number | string> | null;
  LeadId?: number | string | null;
  LeadIds?: Array<number | string> | null;
}

interface LeadHistoryRange {
  from: Moment;
  to: Moment;
  isClampedToHistoryFrom: boolean;
}

const BASE_HISTORY_RANGE_DAYS = 14;
const LATEST_HISTORY_REFRESH_DELAY_MS = 700;
// Границы окон совпадают до секунды, а семантика сравнения dateS на бэке
// неизвестна: запрашиваем окно с запасом в секунду, чтобы строка на стыке
// окон (или ровно на HistoryFrom) гарантированно попала хотя бы в одно окно.
// Дубли на перекрытии схлопывает keyed-merge.
const HISTORY_RANGE_OVERLAP_SECONDS = 1;
// Диапазон уже запрашивался (дедуп requestedRangesRef) — это НЕ конец
// истории; отдельный сентинел, чтобы вызывающие не путали его с данными.
const ALREADY_REQUESTED_RANGE = 'already-requested-range';
const SCROLL_BOTTOM_THRESHOLD = 80;
const WAIT_FOR_CONTAINER_ATTEMPTS = 12;
const LEAD_OBJECT_TYPE = 'Lead';
const CLIENT_OBJECT_TYPE = 'Client';

const getLeadHistoryClientId = (
  leadCardData: GetLeadCardResponse | undefined
): number | null => {
  const clientViewId = leadCardData?.ClientView.Id ?? null;

  if (typeof clientViewId === 'number' && Number.isFinite(clientViewId)) {
    return clientViewId;
  }

  const clientId = leadCardData?.ClientId ?? null;

  return typeof clientId === 'number' && Number.isFinite(clientId)
    ? clientId
    : null;
};

const waitForLayout = async (): Promise<void> => {
  await new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
};

const isScrollable = (container: HTMLDivElement | null): boolean => {
  if (!container) return false;

  return container.scrollHeight > container.clientHeight;
};

const getRangeKey = ({ from, to }: LeadHistoryRange): string =>
  `${formatLeadHistoryBoundary(from)}_${formatLeadHistoryBoundary(to)}`;

const getIsAtBottom = (container: HTMLDivElement | null): boolean => {
  if (!container) return true;

  const distanceFromBottom =
    container.scrollHeight - container.scrollTop - container.clientHeight;

  return distanceFromBottom < SCROLL_BOTTOM_THRESHOLD;
};

const toFiniteNumber = (value: number | string | null | undefined) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const hasMatchingId = (
  values: Array<number | string> | null | undefined,
  allowedIds: Set<number>
): boolean =>
  Array.isArray(values) &&
  values.some((value) => {
    const parsed = toFiniteNumber(value);
    return parsed !== null && allowedIds.has(parsed);
  });

const isLeadHistoryRealtimeItem = (
  value: unknown
): value is LeadCardHistoryRealtimeItem => {
  if (typeof value !== 'object' || value === null) return false;

  const item = value as Partial<LeadCardHistoryRealtimeItem>;

  return typeof item.Timestamp === 'string';
};

const parseLeadHistoryUpdate = (
  jsonData: string
): LeadCardHistoryRealtimeItem[] => {
  const parsed: unknown = JSON.parse(jsonData);

  if (!Array.isArray(parsed)) return [];

  return parsed.filter(isLeadHistoryRealtimeItem);
};

const isHistoryItemMatching = ({
  item,
  selectedLeadId,
  clientIdSet,
}: {
  item: LeadCardHistoryRealtimeItem;
  selectedLeadId: number | null;
  clientIdSet: Set<number>;
}): boolean => {
  const clientId = toFiniteNumber(item.ClientId);
  const leadId = toFiniteNumber(item.LeadId);

  if (clientId !== null && clientIdSet.has(clientId)) return true;
  if (hasMatchingId(item.ClientIds, clientIdSet)) return true;
  if (selectedLeadId !== null && leadId === selectedLeadId) return true;
  if (
    selectedLeadId !== null &&
    hasMatchingId(item.LeadIds, new Set([selectedLeadId]))
  ) {
    return true;
  }
  if (
    item.ObjectType === LEAD_OBJECT_TYPE &&
    selectedLeadId !== null &&
    item.ObjectId === selectedLeadId
  ) {
    return true;
  }
  if (
    item.ObjectType === CLIENT_OBJECT_TYPE &&
    clientIdSet.has(item.ObjectId)
  ) {
    return true;
  }
  if (item.Files?.some(({ ClientId }) => clientIdSet.has(ClientId))) {
    return true;
  }

  return false;
};

const normalizeIncomingHistoryItem = ({
  item,
  selectedLeadId,
  clientIdSet,
}: {
  item: LeadCardHistoryRealtimeItem;
  selectedLeadId: number | null;
  clientIdSet: Set<number>;
}): LeadCardHistoryItemData | null => {
  if (item.ChatMessages?.length) {
    const filteredMessages = item.ChatMessages.filter(({ ClientId }) =>
      clientIdSet.has(ClientId)
    );

    if (filteredMessages.length === 0) return null;

    return { ...item, ChatMessages: filteredMessages };
  }

  return isHistoryItemMatching({ item, selectedLeadId, clientIdSet })
    ? item
    : null;
};

export const useLeadMessageHistory = ({
  leadCardData,
  selectedLeadId,
  isLeadCardPending,
  onRelevantClientHistory,
}: UseLeadMessageHistoryParams) => {
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const requestIdRef = useRef(0);
  const firstSeenSeqRef = useRef<Map<string, number>>(new Map());
  const nextSeqRef = useRef<number>(1);
  const requestedRangesRef = useRef<Set<string>>(new Set());
  const oldestRequestedBoundaryRef = useRef<Moment | null>(null);
  const oldestLoadedOrCheckedBoundaryRef = useRef<Moment | null>(null);
  const rangeIndexRef = useRef(1);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [historyFrom, setHistoryFrom] = useState<string | null>(null);
  const [selectedLeadHistoryItems, setSelectedLeadHistoryItems] = useState<
    LeadCardHistoryItemData[]
  >([]);
  const selectedLeadHistoryItemsRef = useRef<LeadCardHistoryItemData[]>([]);
  // Строки, убранные из истории вручную (удаление комментария): комментарий
  // ещё придёт в ClientComments, пока карточка не перезапросилась, — по этим
  // ключам он не достраивается обратно.
  const [removedHistoryKeys, setRemovedHistoryKeys] = useState<Set<string>>(
    () => new Set()
  );
  const [isInitialHistoryLoading, setIsInitialHistoryLoading] = useState(false);
  const [isOlderHistoryLoading, setIsOlderHistoryLoading] = useState(false);
  const [isAllHistoryLoading, setIsAllHistoryLoading] = useState(false);
  const [isHistoryFullyLoaded, setIsHistoryFullyLoaded] = useState(false);
  const [rangeIndex, setRangeIndex] = useState(1);
  const [isLeadMessageHistoryInitialized, setIsLeadMessageHistoryInitialized] =
    useState(false);
  const [initializedLeadId, setInitializedLeadId] = useState<number | null>(
    null
  );
  const [leadMessageHistoryError, setLeadMessageHistoryError] = useState<
    unknown | null
  >(null);
  const [canLoadOlderHistory, setCanLoadOlderHistory] = useState(false);
  // Отдельный флаг для инлайн-ошибки подгрузки: нотификация может быть
  // подавлена maintenance-режимом, а этот флаг виден в самом чате всегда.
  const [olderHistoryLoadFailed, setOlderHistoryLoadFailed] = useState(false);

  const [getLeadClientHistory] = useLazyGetLeadClientActionHistoryQuery();

  const isLeadMessageHistoryInitializedRef = useRef(false);
  const isOlderHistoryLoadingRef = useRef(false);
  const isAllHistoryLoadingRef = useRef(false);
  const isHistoryFullyLoadedRef = useRef(false);
  const olderHistoryLoadFailedRef = useRef(false);
  const onRelevantClientHistoryRef = useRef(onRelevantClientHistory);

  isLeadMessageHistoryInitializedRef.current = isLeadMessageHistoryInitialized;
  isOlderHistoryLoadingRef.current = isOlderHistoryLoading;
  isAllHistoryLoadingRef.current = isAllHistoryLoading;
  isHistoryFullyLoadedRef.current = isHistoryFullyLoaded;
  olderHistoryLoadFailedRef.current = olderHistoryLoadFailed;
  selectedLeadHistoryItemsRef.current = selectedLeadHistoryItems;
  onRelevantClientHistoryRef.current = onRelevantClientHistory;

  const isLeadCardDataActual =
    selectedLeadId !== null && leadCardData?.LeadId === selectedLeadId;
  const leadHistoryClientId = isLeadCardDataActual
    ? getLeadHistoryClientId(leadCardData)
    : null;
  const actualHistoryFrom = isLeadCardDataActual
    ? leadCardData?.ClientView.HistoryFrom ?? null
    : null;

  const selectedLeadClientIds = useMemo(() => {
    if (!isLeadCardDataActual) return [];

    return Array.from(
      new Set(
        [...(leadCardData?.LeadClientIds ?? []), leadHistoryClientId].filter(
          (clientId): clientId is number =>
            typeof clientId === 'number' && Number.isFinite(clientId)
        )
      )
    );
  }, [isLeadCardDataActual, leadCardData?.LeadClientIds, leadHistoryClientId]);

  const setCurrentRangeIndex = useCallback((nextRangeIndex: number) => {
    rangeIndexRef.current = nextRangeIndex;
    setRangeIndex(nextRangeIndex);
  }, []);

  const resetHistoryRequestState = useCallback(() => {
    firstSeenSeqRef.current = new Map();
    nextSeqRef.current = 1;
    requestedRangesRef.current = new Set();
    oldestRequestedBoundaryRef.current = null;
    oldestLoadedOrCheckedBoundaryRef.current = null;
    setCurrentRangeIndex(1);
  }, [setCurrentRangeIndex]);

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollTop = container.scrollHeight - container.clientHeight;
  }, []);

  const waitForScrollContainer = useCallback(
    async (requestId: number): Promise<HTMLDivElement | null> => {
      for (let attempt = 0; attempt < WAIT_FOR_CONTAINER_ATTEMPTS; attempt++) {
        if (requestIdRef.current !== requestId) return null;
        if (scrollContainerRef.current) return scrollContainerRef.current;

        await waitForLayout();
      }

      return scrollContainerRef.current;
    },
    []
  );

  const fetchHistoryRange = useCallback(
    async (clientId: number, range: LeadHistoryRange) => {
      const rangeKey = getRangeKey(range);

      if (requestedRangesRef.current.has(rangeKey)) {
        return ALREADY_REQUESTED_RANGE;
      }

      requestedRangesRef.current.add(rangeKey);

      const dateS = formatLeadHistoryRangeStart(
        range.from.clone().subtract(HISTORY_RANGE_OVERLAP_SECONDS, 'second')
      );
      const dateE = formatLeadHistoryRangeEnd(range.to);

      try {
        const response = await getLeadClientHistory({
          forClientId: clientId,
          dateS,
          dateE,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        oldestRequestedBoundaryRef.current = range.from.clone();
        oldestLoadedOrCheckedBoundaryRef.current = range.from.clone();

        return Array.isArray(response) ? response : [];
      } catch (error) {
        requestedRangesRef.current.delete(rangeKey);
        throw error;
      }
    },
    [activeBranchId, getLeadClientHistory]
  );

  // Merges an incoming batch into raw history.
  // A batch is skipped only when it has zero new rows AND zero changed duplicates;
  // partial-duplicate batches with at least one new or updated row are still applied.
  const mergeHistoryItems = useCallback((items: LeadCardHistoryItemData[]) => {
    flushSync(() => {
      setSelectedLeadHistoryItems((prev) => {
        if (items.length === 0) return prev;

        const previousByKey = new Map(
          prev.map((item) => [getLeadHistoryStableKey(item), item])
        );
        let newCount = 0;
        let changedDuplicateCount = 0;

        items.forEach((item) => {
          const key = getLeadHistoryStableKey(item);
          const previousItem = previousByKey.get(key);

          if (!previousItem) {
            newCount += 1;
            return;
          }

          if (!isEqual(previousItem, item)) {
            changedDuplicateCount += 1;
          }
        });

        if (newCount === 0 && changedDuplicateCount === 0) return prev;

        return mergeLeadHistoryByStableKey(
          prev,
          items,
          firstSeenSeqRef.current,
          nextSeqRef
        );
      });
    });
  }, []);

  const completeInitialization = useCallback(async () => {
    flushSync(() => {
      setIsInitialHistoryLoading(false);
      setInitializedLeadId(selectedLeadId);
      setIsLeadMessageHistoryInitialized(true);
    });

    await waitForLayout();
    scrollToBottom();
  }, [scrollToBottom, selectedLeadId]);

  // История склеивается мержем (mergeHistoryItems только добавляет и обновляет
  // строки), поэтому запись, удалённая на сервере, сама из списка не пропадёт:
  // её нужно убрать явно — например, после удаления комментария клиента
  // (issue/10992).
  const removeHistoryItem = useCallback(
    (objectType: string, objectId: number) => {
      setSelectedLeadHistoryItems((prev) => {
        const next = prev.filter(
          (item) =>
            !(item.ObjectType === objectType && item.ObjectId === objectId)
        );

        return next.length === prev.length ? prev : next;
      });

      if (objectType !== LEAD_COMMENT_OBJECT_TYPE) return;

      setRemovedHistoryKeys((prev) => {
        const key = getLeadCommentHistoryKey(objectId);

        if (prev.has(key)) return prev;

        const next = new Set(prev);
        next.add(key);

        return next;
      });
    },
    []
  );

  const refetchLeadHistory = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    // Пока карточка лида в промежуточной фазе загрузки, видимое состояние
    // не трогаем: перезапуск на pending-тоггле стирал уже показанную ошибку
    // загрузки истории, а финальный прогон мог завершиться ранним выходом —
    // чат оставался «пустым» без ошибки и кнопки повтора (issue/10449).
    // Список в это время закрыт скелетоном (isMessagesLoading), устаревшие
    // строки не видны.
    if (isLeadCardPending) {
      setIsInitialHistoryLoading(false);
      return;
    }

    resetHistoryRequestState();
    setHistoryFrom(actualHistoryFrom);
    setSelectedLeadHistoryItems([]);
    setRemovedHistoryKeys(new Set());
    setIsHistoryFullyLoaded(false);
    setCanLoadOlderHistory(false);
    olderHistoryLoadFailedRef.current = false;
    setOlderHistoryLoadFailed(false);
    setLeadMessageHistoryError(null);
    setIsLeadMessageHistoryInitialized(false);
    setInitializedLeadId(null);
    setIsOlderHistoryLoading(false);
    setIsAllHistoryLoading(false);

    const historyFromMoment = getValidHistoryFromMoment(actualHistoryFrom);

    if (!leadHistoryClientId || !actualHistoryFrom || !historyFromMoment) {
      setIsHistoryFullyLoaded(true);
      setIsInitialHistoryLoading(false);
      setInitializedLeadId(selectedLeadId);
      setIsLeadMessageHistoryInitialized(true);
      return;
    }

    setIsInitialHistoryLoading(true);

    try {
      const container = await waitForScrollContainer(requestId);
      let currentBoundary = getLeadHistoryNow();
      let reachedHistoryFrom = false;

      while (requestIdRef.current === requestId) {
        const range = getPreviousHistoryRange({
          currentBoundary,
          historyFrom: actualHistoryFrom,
          baseRangeDays: BASE_HISTORY_RANGE_DAYS,
          rangeIndex: rangeIndexRef.current,
        });

        if (!range) {
          reachedHistoryFrom = true;
          break;
        }

        const response = await fetchHistoryRange(leadHistoryClientId, range);

        if (requestIdRef.current !== requestId) return;
        if (response === ALREADY_REQUESTED_RANGE) {
          currentBoundary = range.from.clone();
          reachedHistoryFrom = range.isClampedToHistoryFrom;
          if (reachedHistoryFrom) break;
          continue;
        }

        currentBoundary = range.from.clone();
        reachedHistoryFrom = range.isClampedToHistoryFrom;

        if (response.length > 0) {
          mergeHistoryItems(response);
          setCurrentRangeIndex(1);
          await waitForLayout();
        } else if (!reachedHistoryFrom) {
          setCurrentRangeIndex(rangeIndexRef.current * 2);
        }

        if (isScrollable(container) || reachedHistoryFrom) {
          break;
        }
      }

      if (requestIdRef.current !== requestId) return;

      setIsHistoryFullyLoaded(reachedHistoryFrom);
      setCanLoadOlderHistory(!reachedHistoryFrom);
      await completeInitialization();
    } catch (error) {
      if (requestIdRef.current !== requestId) return;

      setSelectedLeadHistoryItems([]);
      setLeadMessageHistoryError(error);
      setIsInitialHistoryLoading(false);
      setInitializedLeadId(selectedLeadId);
      setIsLeadMessageHistoryInitialized(true);
      showNotification(error, WARNING);
    }
  }, [
    actualHistoryFrom,
    completeInitialization,
    fetchHistoryRange,
    isLeadCardPending,
    leadHistoryClientId,
    mergeHistoryItems,
    resetHistoryRequestState,
    setCurrentRangeIndex,
    showNotification,
    selectedLeadId,
    waitForScrollContainer,
  ]);

  const loadOlderHistory = useCallback(async () => {
    if (
      !isLeadMessageHistoryInitializedRef.current ||
      isOlderHistoryLoadingRef.current ||
      isAllHistoryLoadingRef.current ||
      isHistoryFullyLoadedRef.current ||
      !canLoadOlderHistory
    ) {
      return;
    }

    const requestId = requestIdRef.current;
    const historyBoundary = oldestLoadedOrCheckedBoundaryRef.current;

    if (!leadHistoryClientId || !actualHistoryFrom || !historyBoundary) return;

    const container = scrollContainerRef.current;
    const previousScrollTop = container?.scrollTop ?? 0;
    const previousScrollHeight = container?.scrollHeight ?? 0;

    // Ref ставится синхронно: state-версия обновит его только на следующем
    // рендере, а повторный вызов (скролл + ResizeObserver) может прийти раньше.
    isOlderHistoryLoadingRef.current = true;
    setIsOlderHistoryLoading(true);
    olderHistoryLoadFailedRef.current = false;
    setOlderHistoryLoadFailed(false);
    setCurrentRangeIndex(1);

    let currentBoundary = historyBoundary.clone();
    let reachedHistoryFrom = false;

    try {
      while (requestIdRef.current === requestId) {
        const range = getPreviousHistoryRange({
          currentBoundary,
          historyFrom: actualHistoryFrom,
          baseRangeDays: BASE_HISTORY_RANGE_DAYS,
          rangeIndex: rangeIndexRef.current,
        });

        if (!range) {
          reachedHistoryFrom = true;
          break;
        }

        const response = await fetchHistoryRange(leadHistoryClientId, range);

        if (requestIdRef.current !== requestId) return;
        if (response === ALREADY_REQUESTED_RANGE) {
          currentBoundary = range.from.clone();
          reachedHistoryFrom = range.isClampedToHistoryFrom;
          if (reachedHistoryFrom) break;
          continue;
        }

        currentBoundary = range.from.clone();
        reachedHistoryFrom = range.isClampedToHistoryFrom;

        if (response.length > 0) {
          mergeHistoryItems(response);

          if (container) {
            container.scrollTop =
              previousScrollTop +
              (container.scrollHeight - previousScrollHeight);
          }

          setCurrentRangeIndex(1);
          break;
        }

        if (!reachedHistoryFrom) {
          setCurrentRangeIndex(rangeIndexRef.current * 2);
        }

        if (reachedHistoryFrom) break;
      }

      if (requestIdRef.current !== requestId) return;

      setIsHistoryFullyLoaded(reachedHistoryFrom);
      setCanLoadOlderHistory(!reachedHistoryFrom);
    } catch (error) {
      if (requestIdRef.current !== requestId) return;

      olderHistoryLoadFailedRef.current = true;
      setOlderHistoryLoadFailed(true);
      showNotification(error, WARNING);
    } finally {
      // Сброс только своего поколения: finally устаревшего запроса (после
      // смены лида) не должен снимать флаг, выставленный уже новым вызовом.
      if (requestIdRef.current === requestId) {
        isOlderHistoryLoadingRef.current = false;
        setIsOlderHistoryLoading(false);
      }
    }
  }, [
    actualHistoryFrom,
    canLoadOlderHistory,
    fetchHistoryRange,
    leadHistoryClientId,
    mergeHistoryItems,
    setCurrentRangeIndex,
    showNotification,
  ]);

  // Loads every remaining older range in one action (the "load all history"
  // button), instead of one batch per scroll-up. Reuses the same range walk as
  // loadOlderHistory but keeps going until it reaches the client's HistoryFrom.
  const loadAllHistory = useCallback(async () => {
    if (
      !isLeadMessageHistoryInitializedRef.current ||
      isOlderHistoryLoadingRef.current ||
      isAllHistoryLoadingRef.current ||
      isHistoryFullyLoadedRef.current ||
      !canLoadOlderHistory
    ) {
      return;
    }

    const requestId = requestIdRef.current;
    const historyBoundary = oldestLoadedOrCheckedBoundaryRef.current;

    if (!leadHistoryClientId || !actualHistoryFrom || !historyBoundary) return;

    const container = scrollContainerRef.current;
    const previousScrollTop = container?.scrollTop ?? 0;
    const previousScrollHeight = container?.scrollHeight ?? 0;

    isAllHistoryLoadingRef.current = true;
    setIsAllHistoryLoading(true);
    olderHistoryLoadFailedRef.current = false;
    setOlderHistoryLoadFailed(false);
    setCurrentRangeIndex(1);

    let currentBoundary = historyBoundary.clone();
    let reachedHistoryFrom = false;

    try {
      while (requestIdRef.current === requestId) {
        const range = getPreviousHistoryRange({
          currentBoundary,
          historyFrom: actualHistoryFrom,
          baseRangeDays: BASE_HISTORY_RANGE_DAYS,
          rangeIndex: rangeIndexRef.current,
        });

        if (!range) {
          reachedHistoryFrom = true;
          break;
        }

        const response = await fetchHistoryRange(leadHistoryClientId, range);

        if (requestIdRef.current !== requestId) return;
        if (response === ALREADY_REQUESTED_RANGE) {
          currentBoundary = range.from.clone();
          reachedHistoryFrom = range.isClampedToHistoryFrom;
          if (reachedHistoryFrom) break;
          continue;
        }

        currentBoundary = range.from.clone();
        reachedHistoryFrom = range.isClampedToHistoryFrom;

        if (response.length > 0) {
          mergeHistoryItems(response);

          if (container) {
            // Keep the originally viewed messages anchored while older ones are
            // prepended, so the viewport does not jump during the bulk load.
            container.scrollTop =
              previousScrollTop +
              (container.scrollHeight - previousScrollHeight);
          }

          setCurrentRangeIndex(1);
        } else if (!reachedHistoryFrom) {
          setCurrentRangeIndex(rangeIndexRef.current * 2);
        }

        if (reachedHistoryFrom) break;
      }

      if (requestIdRef.current !== requestId) return;

      setIsHistoryFullyLoaded(reachedHistoryFrom);
      setCanLoadOlderHistory(!reachedHistoryFrom);
    } catch (error) {
      if (requestIdRef.current !== requestId) return;

      olderHistoryLoadFailedRef.current = true;
      setOlderHistoryLoadFailed(true);
      showNotification(error, WARNING);
    } finally {
      if (requestIdRef.current === requestId) {
        isAllHistoryLoadingRef.current = false;
        setIsAllHistoryLoading(false);
      }
    }
  }, [
    actualHistoryFrom,
    canLoadOlderHistory,
    fetchHistoryRange,
    leadHistoryClientId,
    mergeHistoryItems,
    setCurrentRangeIndex,
    showNotification,
  ]);

  // The WebSocket OnClientHistoryUpdate event is unreliable for some lead
  // actions, so we fetch the latest day range explicitly after a successful
  // mutation and rely on mergeHistoryItems to apply the new row even when the
  // response also contains existing rows.
  const refreshLatestHistory = useCallback(
    async (messageTimestamps: string[] = []) => {
      const requestId = requestIdRef.current;
      const historyFromMoment = getValidHistoryFromMoment(actualHistoryFrom);
      const shouldScrollToBottom = getIsAtBottom(scrollContainerRef.current);

      await new Promise((resolve) => {
        setTimeout(resolve, LATEST_HISTORY_REFRESH_DELAY_MS);
      });

      if (requestIdRef.current !== requestId) return;

      if (
        !leadHistoryClientId ||
        !actualHistoryFrom ||
        !historyFromMoment ||
        isLeadCardPending
      ) {
        return;
      }

      const to = getLeadHistoryNow().add(1, 'minute');
      const candidateFrom = getLeadHistoryNow().subtract(1, 'day');
      const from = candidateFrom.isBefore(historyFromMoment)
        ? historyFromMoment.clone()
        : candidateFrom;

      try {
        const response = await getLeadClientHistory({
          forClientId: leadHistoryClientId,
          dateS: formatLeadHistoryRangeStart(
            from.clone().subtract(HISTORY_RANGE_OVERLAP_SECONDS, 'second')
          ),
          dateE: formatLeadHistoryRangeEnd(to),
          ActiveBranchId: activeBranchId,
        }).unwrap();

        if (requestIdRef.current !== requestId) return;

        const incoming = Array.isArray(response) ? response : [];
        const previousHistory = selectedLeadHistoryItemsRef.current;
        const previousKeys = new Set(
          previousHistory.map(getLeadHistoryStableKey)
        );
        const newCount = incoming.reduce(
          (acc, item) =>
            previousKeys.has(getLeadHistoryStableKey(item)) ? acc : acc + 1,
          0
        );

        mergeHistoryItems(incoming);

        // Read acknowledgements can concern an older visible bubble. Refresh
        // only its day, not the entire gap between that message and today.
        const olderDays = new Map<string, Moment>();
        for (const timestamp of messageTimestamps) {
          const date = getValidHistoryFromMoment(timestamp);
          if (date && date.isBefore(from))
            olderDays.set(date.format('YYYY-MM-DD'), date);
        }
        for (const day of olderDays.values()) {
          if (requestIdRef.current !== requestId) return;
          const older = await getLeadClientHistory({
            forClientId: leadHistoryClientId,
            dateS: formatLeadHistoryRangeStart(day.clone().startOf('day')),
            dateE: formatLeadHistoryRangeEnd(day.clone().endOf('day')),
            ActiveBranchId: activeBranchId,
          }).unwrap();
          if (requestIdRef.current !== requestId) return;
          mergeHistoryItems(Array.isArray(older) ? older : []);
        }

        if (shouldScrollToBottom && newCount > 0) {
          setTimeout(scrollToBottom, 0);
        }
      } catch {
        // The targeted refresh is best-effort; a failure should not block the
        // assignee update flow itself.
      }
    },
    [
      activeBranchId,
      actualHistoryFrom,
      getLeadClientHistory,
      isLeadCardPending,
      leadHistoryClientId,
      mergeHistoryItems,
      scrollToBottom,
    ]
  );

  const ensureHistoryFitsViewport = useCallback(async () => {
    const container = scrollContainerRef.current;

    // olderHistoryLoadFailedRef останавливает автоподгрузку после первой
    // ошибки: без этого гарда каждый сбой перезапускал бы её через
    // rAF/ResizeObserver бесконечно. Продолжение — только ручное «Повторить»
    // (loadOlderHistory сбрасывает флаг на входе).
    if (
      !isLeadMessageHistoryInitializedRef.current ||
      isOlderHistoryLoadingRef.current ||
      isHistoryFullyLoadedRef.current ||
      olderHistoryLoadFailedRef.current ||
      isScrollable(container)
    ) {
      return;
    }

    await loadOlderHistory();
  }, [loadOlderHistory]);

  const onClientHistoryUpdateHandler = useCallback(
    (data: { jsonData: string }) => {
      const clientIdSet = new Set(selectedLeadClientIds);
      const container = scrollContainerRef.current;
      const shouldScrollToBottom =
        isLeadMessageHistoryInitializedRef.current &&
        !isOlderHistoryLoadingRef.current &&
        getIsAtBottom(container);

      let incoming: LeadCardHistoryRealtimeItem[];

      try {
        incoming = parseLeadHistoryUpdate(data.jsonData);
      } catch {
        return;
      }

      if (incoming.length === 0) return;

      const filtered = incoming.reduce<LeadCardHistoryItemData[]>(
        (acc, item) => {
          const normalized = normalizeIncomingHistoryItem({
            item,
            selectedLeadId,
            clientIdSet,
          });

          if (normalized) acc.push(normalized);

          return acc;
        },
        []
      );

      if (!filtered.length) return;

      mergeHistoryItems(filtered);
      onRelevantClientHistoryRef.current?.();

      if (shouldScrollToBottom) {
        setTimeout(scrollToBottom, 0);
      }
    },
    [mergeHistoryItems, scrollToBottom, selectedLeadClientIds, selectedLeadId]
  );

  useEffect(() => {
    void refetchLeadHistory();
  }, [refetchLeadHistory]);

  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
    };
  }, []);

  // Комментарии клиента приходят в карточке целиком
  // (GetLeadCard.ClientComments), а строками истории — только те, что попали в
  // загруженные окна. Недостающие достраиваются до строк истории: карточка
  // показывает все комментарии, не дожидаясь прогрузки истории.
  const historyItemsWithComments = useMemo(
    () =>
      mergeLeadCommentHistoryItems({
        items: selectedLeadHistoryItems,
        comments: isLeadCardDataActual ? leadCardData?.ClientComments : null,
        removedKeys: removedHistoryKeys,
      }),
    [
      isLeadCardDataActual,
      leadCardData?.ClientComments,
      removedHistoryKeys,
      selectedLeadHistoryItems,
    ]
  );

  // Закреплённые комментарии старше загруженной истории: лента держит их
  // стопкой сверху, а не разворачивает в начале каждой порции (issue/11369).
  const pinnedCommentIdsAheadOfHistory = useMemo(
    () =>
      getPinnedCommentsAheadOfHistory({
        items: selectedLeadHistoryItems,
        comments: isLeadCardDataActual ? leadCardData?.ClientComments : null,
        isHistoryFullyLoaded,
        removedKeys: removedHistoryKeys,
      }),
    [
      isHistoryFullyLoaded,
      isLeadCardDataActual,
      leadCardData?.ClientComments,
      removedHistoryKeys,
      selectedLeadHistoryItems,
    ]
  );

  // Признак «есть исходящие сообщения, ждущие подтверждения» — по нему
  // effects-хук включает страховочный поллинг статусов (WS-событие о смене
  // статуса может потеряться, и «часики» иначе залипают навсегда).
  const hasPendingOutgoingMessages = useMemo(
    () =>
      selectedLeadHistoryItems.some((item) => {
        const chatMessage = item.ChatMessages?.[0];

        return Boolean(
          chatMessage &&
            chatMessage.Direction !== 'in' &&
            chatMessage.Status === LEAD_MSG_STATUS_PENDING
        );
      }),
    [selectedLeadHistoryItems]
  );

  return useMemo(
    () => ({
      historyFrom,
      selectedLeadHistoryItems: historyItemsWithComments,
      pinnedCommentIdsAheadOfHistory,
      selectedLeadClientIds,
      hasPendingOutgoingMessages,
      isLeadMessageHistoryLoading: isLeadCardPending || isInitialHistoryLoading,
      isInitialHistoryLoading,
      isOlderHistoryLoading,
      isAllHistoryLoading,
      isHistoryFullyLoaded,
      isLeadMessageHistoryInitialized,
      isLeadMessageHistoryCurrent:
        selectedLeadId !== null &&
        isLeadMessageHistoryInitialized &&
        initializedLeadId === selectedLeadId,
      leadMessageHistoryError,
      canLoadOlderHistory,
      olderHistoryLoadFailed,
      oldestRequestedBoundary: oldestRequestedBoundaryRef.current,
      oldestLoadedOrCheckedBoundary: oldestLoadedOrCheckedBoundaryRef.current,
      rangeIndex,
      scrollContainerRef,
      scrollToBottom,
      refetchLeadHistory,
      refreshLatestHistory,
      removeHistoryItem,
      loadOlderHistory,
      loadAllHistory,
      ensureHistoryFitsViewport,
      onClientHistoryUpdateHandler,
    }),
    [
      canLoadOlderHistory,
      ensureHistoryFitsViewport,
      hasPendingOutgoingMessages,
      historyFrom,
      isHistoryFullyLoaded,
      isInitialHistoryLoading,
      isLeadCardPending,
      isLeadMessageHistoryInitialized,
      initializedLeadId,
      isOlderHistoryLoading,
      isAllHistoryLoading,
      leadMessageHistoryError,
      loadOlderHistory,
      loadAllHistory,
      olderHistoryLoadFailed,
      onClientHistoryUpdateHandler,
      pinnedCommentIdsAheadOfHistory,
      rangeIndex,
      refetchLeadHistory,
      refreshLatestHistory,
      removeHistoryItem,
      scrollToBottom,
      selectedLeadClientIds,
      selectedLeadId,
      historyItemsWithComments,
    ]
  );
};
